/**
 * CallTrackingMetrics (CTM) Fixture Generator (Story 22.4 — closes Epic 22)
 *
 * Generates AdapterMetrics matching Story 22.3's `fetchMetrics()` shape for
 * 1 demo client (Summit Legal Group — the only Apex Digital client where
 * CTM fits per `fixture-data-directive.md` line 23 local-services mapping).
 * Closes Epic 22 — after this lands, CallRail (22.1+22.2) + CTM (22.3+22.4)
 * are shipped at full ProvenLeap parity.
 *
 * Architecture:
 *   - Imports the 3 transformers from `../../ctm/transformers` and
 *     constructs each row by passing a synthetic raw CTM v1 wire-format
 *     snake_case JSON object through the production transformer. This
 *     guarantees the fixture record shape can NEVER drift from production
 *     unless the transformer itself changes — in which case the import-
 *     side fails CI.
 *   - Single-account / single-clientSlug (NO surface dispatch like
 *     Amazon's `_adProduct`). CTM is single-account-scoped after the
 *     picker step.
 *   - 4 `_reportType` discriminators: calls-list, form-submissions, sms
 *     (3 wire-format primary widget-bound) + accounts-list (1 minimum-
 *     stub for `discoverEndpoints` probe; no widget binding in 22.4).
 *
 * Privacy invariants (Round 1 Privacy Officer + Round 3 Red Team #3 — SECOND
 * fixture in our library with customer PII flowing through fixtures after
 * CallRail 22.2; CRITICAL — DISJOINT FCC sub-pool from CallRail):
 *   - `caller_number` / `caller_number_format` uses the FCC-reserved
 *     `555-0200/0299` fictional-phone sub-block per
 *     `www.fcc.gov/consumer-governmental-affairs-bureau/consumer-protection
 *     -and-government-affairs/fictitious-telephone-numbers` (Atlanta area
 *     code 404). DISJOINT from CallRail 22.2's `555-0100/0199` so the
 *     cross-platform PII-disjointness test passes when both fixtures
 *     coexist in the demo workspace.
 *       - Calls-list:        0200..0229 (30 numbers — 65 calls reuse with
 *                            ~2× duplication for repeat callers)
 *       - Form submissions:  0230..0249 (20 unique form submitters)
 *       - SMS:               0250..0279 (12 distinct conversation threads
 *                            across 25 messages — some phones recur)
 *       - Tracking numbers:  0280..0299 (20-number tracking sub-pool)
 *   - `name` uses curated `Caller AA`..`Caller ZZ` list (DISJOINT from
 *     CallRail's `Customer A..Z4` pool) — fresh seed offset prevents
 *     collisions when both fixtures render side-by-side.
 *   - Form `name` uses `Caller-FA`..`Caller-FT` (fresh offset, DISJOINT
 *     from CTM calls-list AND CallRail form pools).
 *   - `email` redacted to literal `[redacted]` in every row.
 *   - `city` from 5-city Atlanta-metro allowlist (Atlanta / Marietta /
 *     Decatur / Sandy Springs / Roswell).
 *   - `state` always `GA`; `country` always `US`.
 *   - `summary` + `transcription` + SMS `message` body from curated
 *     templated lists — NEVER real cases, NEVER real PII embedded.
 *
 * SMS body PII redaction (NEW for Story 22.4 — Round 2 Pre-mortem E + Round
 * 3 Red Team Attack #1 — SMS bodies are the highest-PII surface in any
 * ProvenLeap fixture to date):
 *   - 5-template inbound list (generic caller questions)
 *   - 3-template outbound list (FormReactor-style auto-replies, `{date}` /
 *     `{time}` left as LITERAL placeholder strings — never interpolated
 *     real values).
 *
 * Audio-URL-stripped invariant (Round 3 Red Team Attack #2 — Story 22.3 AC
 * #12 carry-forward + CallRail 22.2 AC #6 mirror):
 *   - The fixture explicitly OMITS the `audio` field on every calls-list
 *     row. The Story 22.3 transformer strips it at production path; the
 *     fixture mirrors that omission for cross-tenant URL-leak prevention.
 *
 * CSV-injection-stripped + HTML-escape invariants (Round 3 Red Team #4 + #5):
 *   - All string-valued fields pass through the transformer's
 *     `stripCsvInjectionPrefix` AND we apply `htmlEscape` to user-
 *     displayable fields (`name`, `city`, `summary`, `transcription`,
 *     `message`, `source`, `campaign`, `keyword`, etc.).
 *
 * No retention clamp (DIVERGENT from CallRail 22.2's 24-month clamp):
 *   - CTM has no documented data-retention ceiling per
 *     `www.calltrackingmetrics.com/legal/data-protection-addendum-2025/`
 *     verified 2026-05-15. The fixture clamps dates to a simple
 *     90-day window from `fixtureGeneratedAt`. NO 25-month equivalent
 *     to CallRail's hard retention pre-flight.
 *
 * NO-DELIVERY-RECEIPT-FIELDS invariant (Round 5 Edge Case Hunter — SMS):
 *   - The catalogue at `_bmad/bmm/data/integration-metrics/calltrackingmetrics.md`
 *     does NOT list delivery-status fields (`delivered_at`, `delivery_status`,
 *     `failed_reason`); Story 22.3 did NOT ship them; Story 22.4 fixture
 *     explicitly DOES NOT emit them. A future agent must not "fix" the
 *     fixture by adding plausible-looking delivery fields.
 *
 * Determinism (Round 5 Critique and Refine — AC #17):
 *   - Every random value flows through `createRng(config.seed)`. Re-running
 *     with the same `(platform, clientSlug, dateRange, seed)` yields
 *     byte-identical output (modulo `fixtureGeneratedAt` if `Date.now()`
 *     is not mocked).
 *
 * Source-of-truth references:
 *   - `_bmad/bmm/data/integration-metrics/calltrackingmetrics.md` — 74-metric catalogue
 *   - `_bmad/bmm/data/fixture-data-directive.md` — realism rules
 *   - `packages/integrations/src/ctm/transformers.ts` — record shape
 *   - `packages/integrations/src/ctm/ctm.ts` — adapter contract
 *   - `packages/integrations/src/fixtures/generators/callrail.ts` — Story
 *     22.2 canonical template (CTM mirrors with 5 divergences documented
 *     in this story's spec)
 */

import type { AdapterMetrics } from "../../adapter";
import type { FixtureConfig } from "../types";
import { createRng, randomInt, randomFloat } from "../rng";
import {
  transformCtmCallRow,
  transformCtmFormSubmissionRow,
  transformCtmSmsRow,
} from "../../ctm/transformers";

// ============================================================================
// Constants
// ============================================================================

/** Fixture schema version — bump when generator output shape changes. */
export const CTM_FIXTURE_SCHEMA_VERSION = "22.4.0";

/** Account ID stamped on every fixture record (matches demo-accounts.ts). */
export const CTM_SUMMIT_LEGAL_ACCOUNT_ID = "demo-810002";

/** Account display name stamped on metadata. */
export const CTM_SUMMIT_LEGAL_ACCOUNT_NAME =
  "Summit Legal Group (Demo CTM)";

/** Summit Legal target domain (used in form URLs and similar). */
export const CTM_SUMMIT_LEGAL_TARGET = "summitlegalgroup.com";

/**
 * Atlanta-metro generic-city allowlist for the `city` field. Same 5-city
 * pool as CallRail 22.2 — keeps demo geography consistent across both
 * adapters when rendered side-by-side.
 */
export const CTM_FIXTURE_CITIES: ReadonlyArray<string> = [
  "Atlanta",
  "Marietta",
  "Decatur",
  "Sandy Springs",
  "Roswell",
];

/**
 * 5-template realistic-but-generic transcription bank. NEVER references
 * real cases or real legal-issue content (Round 1 Privacy Officer).
 */
export const CTM_FIXTURE_TRANSCRIPTIONS: ReadonlyArray<string> = [
  "Customer: Hi, I'd like to speak with an attorney about my recent accident. Agent: Thank you for calling Summit Legal, I'll connect you with our intake coordinator.",
  "Customer: I am looking for information on a possible legal case. Agent: I can take some basic details and schedule a consultation for you.",
  "Customer: Can I schedule a consultation this week? Agent: Yes, let me check our calendar and offer a few times.",
  "Customer: I have a question about an active matter. Agent: I'll connect you with the paralegal handling that case.",
  "Customer: How does your firm structure fees? Agent: We offer contingency fees for qualifying cases; let me explain how that works.",
];

/**
 * 5-template realistic-but-generic call-summary bank. Used for the
 * `summary` field on CI-entitled answered rows.
 */
export const CTM_FIXTURE_SUMMARIES: ReadonlyArray<string> = [
  "Caller inquired about a personal injury case and was directed to the intake coordinator.",
  "Caller asked about scheduling a consultation. Intake confirmed availability next week.",
  "Existing client checked status on a pending matter. Paralegal returned the call with an update.",
  "Caller requested information on legal fees. Intake explained the contingency-fee structure.",
  "Caller was screened for case eligibility and provided a brief description of their situation.",
];

/**
 * 5-template inbound SMS body bank (Round 2 Pre-mortem E + Round 3 Red
 * Team Attack #1 — NEW for Story 22.4). Generic caller questions, NEVER
 * real PII, NEVER real case details.
 */
export const CTM_FIXTURE_INBOUND_SMS_MESSAGES: ReadonlyArray<string> = [
  "Yes, I'd like to schedule a consultation.",
  "What time works for Monday?",
  "Please call me back.",
  "How much do you charge?",
  "I have a follow-up question about my case.",
];

/**
 * 3-template outbound SMS body bank (FormReactor-style auto-replies).
 * `{date}` and `{time}` are LITERAL placeholder strings, never
 * interpolated real values.
 */
export const CTM_FIXTURE_OUTBOUND_SMS_MESSAGES: ReadonlyArray<string> = [
  "Thank you for contacting Summit Legal Group. We've received your request and will reach out within 24 hours.",
  "Your consultation is confirmed for {date} at {time}. Please reply YES to confirm.",
  "This is a reminder of your upcoming appointment with Summit Legal Group.",
];

/**
 * Curated calls-list `name` list. 30 unique callers matching
 * `/^Caller [A-Z][A-Z]$/` — sized to match the calls-list FCC phone
 * sub-pool of 30 unique numbers (since `phoneSlot = callIndex % 30` binds
 * name to phone-slot for demo realism; see line 2570). DISJOINT from
 * CallRail's `Customer ...` pool. Round 3 Red Team Attack #3 + AC #17
 * cross-platform PII-disjointness.
 *
 * Code Review Round 1 Blind Hunter #13 — sized down from 65 to 30 to
 * eliminate the 35-name dead-pool that a future agent would mistake for
 * coverage. If the phone-slot count ever increases beyond 30, this pool
 * must grow in lockstep.
 */
function buildCallNames(): ReadonlyArray<string> {
  const names: string[] = [];
  const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  outer: for (const c1 of ALPHA) {
    for (const c2 of ALPHA) {
      names.push(`Caller ${c1}${c2}`);
      if (names.length >= 30) break outer;
    }
  }
  return names;
}
export const CTM_FIXTURE_CALL_NAMES: ReadonlyArray<string> = buildCallNames();

/**
 * Form-submission `name` pool — DISJOINT from calls-list. Matches
 * `/^Caller-[A-Z][A-Z]$/` with a dash prefix to mirror CallRail 22.2's
 * `Customer-AA` form-pool convention.
 */
// Code Review Round 1 Blind Hunter #11 — flattened the vestigial 2-D outer
// loop (`FIRSTS = ["F"]` single-element outer iteration). Now a single loop
// over A..T with the literal `F` prefix preserved as the spec-mandated
// form-pool offset (DISJOINT from CTM calls-list `Caller AA-ZZ` and from
// CallRail's `Customer*` pools per AC #5 + AC #17 cross-platform PII-
// disjointness invariant).
function buildFormNames(): ReadonlyArray<string> {
  const names: string[] = [];
  const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  for (const c of ALPHA) {
    if (names.length >= 20) break;
    names.push(`Caller-F${c}`);
  }
  return names;
}
export const CTM_FIXTURE_FORM_NAMES: ReadonlyArray<string> = buildFormNames();

/**
 * HTML-escape a string for safe insertion into widgets that render the
 * value via DOM. Round 3 Red Team Attack #4 invariant.
 *
 * OWASP 6-character set (`&`, `<`, `>`, `"`, `'`, `` ` ``). Byte-identical
 * with the CallRail / Moz / Semrush / Ahrefs helpers.
 */
export function htmlEscape(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/`/g, "&#96;");
}

/**
 * Strip ALL leading CSV/Excel formula-injection trigger characters
 * (`=`, `+`, `-`, `@`, tab, CR) from a string before it's emitted.
 * Defense-in-depth — the transformer already applies this via
 * `stripCsvInjectionPrefix` from `ctm/pii.ts`, but we re-apply at
 * fixture-construction time for clarity.
 */
export function stripCsvInjectionPrefix(input: string): string {
  return input.replace(/^[=+\-@\t\r]+/, "");
}

// ============================================================================
// Source distribution
// ============================================================================

interface CtmSourceDef {
  readonly slug: string;
  readonly sourceLabel: string;
  readonly callCount: number;
  readonly answeredCount: number;
  readonly hasGclid: boolean;
  readonly paidMedium: string;
}

/** AC #4 source-distribution invariant: 23/16/13/8/5 across 5 sources. */
const CTM_FIXTURE_SOURCES: ReadonlyArray<CtmSourceDef> = [
  {
    slug: "google-paid",
    sourceLabel: "Google Paid",
    callCount: 23,
    answeredCount: 21,
    hasGclid: true,
    paidMedium: "cpc",
  },
  {
    slug: "google-organic",
    sourceLabel: "Google Organic",
    callCount: 16,
    answeredCount: 15,
    hasGclid: false,
    paidMedium: "organic",
  },
  {
    slug: "google-business-profile",
    sourceLabel: "Google Business Profile",
    callCount: 13,
    answeredCount: 12,
    hasGclid: false,
    paidMedium: "organic",
  },
  {
    slug: "microsoft-paid",
    sourceLabel: "Microsoft Paid",
    callCount: 8,
    answeredCount: 6,
    hasGclid: false,
    paidMedium: "cpc",
  },
  {
    slug: "direct",
    sourceLabel: "Direct",
    callCount: 5,
    answeredCount: 2,
    hasGclid: false,
    paidMedium: "(none)",
  },
];

/**
 * Form-name distribution per AC #5: 3 FormReactor configurations.
 * Names prefixed `"FormReactor:"` to distinguish from CallRail's plain
 * form names when both render side-by-side (Round 1 UX invariant).
 */
const CTM_FIXTURE_FORM_TYPES: ReadonlyArray<{
  name: string;
  count: number;
}> = [
  { name: "FormReactor: Free Case Evaluation", count: 12 },
  { name: "FormReactor: Contact Us", count: 6 },
  { name: "FormReactor: Schedule Consultation", count: 2 },
];

// ============================================================================
// Helpers — PII-safe FCC fictional-phone sub-pools
// ============================================================================

/**
 * Build a deterministic FCC-fictional phone number from an absolute index
 * in [0, 99] mapped to the CTM-reserved sub-block `555-0200..0299`. NaN /
 * non-finite inputs collapse to index 0.
 *
 * **Code Review Round 1 Auditor #4 — leading `+` strip note.** The
 * generator emits E.164-style `+14045550NNN` strings, but Story 22.3's
 * `transformCtmCallRow` passes every string through
 * `stripCsvInjectionPrefix` from `ctm/pii.ts`, which strips leading
 * `[=+@\-\t\r\n]` characters (CSV/Excel formula-injection defense). The
 * `+` is intentionally in the trigger set because real-world customer-typed
 * `+CMD(...)` payloads would otherwise execute as formulas on export.
 * Consequence: post-transformer, every CTM phone field is emitted as
 * `14045550NNN` (no leading `+`). Tests assert the post-transformer form.
 * Spec AC #4's `/^\+1404...$/` regex predates this discovery; treat the
 * AC as describing the wire-format input shape, not the persisted-record
 * output shape.
 *
 * The FCC fictional-phone block runs 555-0100..555-0199 (CallRail) +
 * 555-0200..555-0299 (CTM, NEW disjoint sub-block per AC #4 — Story 22.4
 * inaugurates this allocation).
 *
 * Disjoint sub-pools (CTM cross-platform PII-disjointness invariant
 * AC #17):
 *   - Calls-list:        indices 0..29  → `+1404555{0200..0229}`
 *   - Form-submissions:  indices 30..49 → `+1404555{0230..0249}`
 *   - SMS:               indices 50..79 → `+1404555{0250..0279}`
 *   - Tracking numbers:  indices 80..99 → `+1404555{0280..0299}`
 *
 * Returns the E.164 form `+14045550NNN` where NNN ∈ [200, 299].
 */
function buildFccPhoneE164(absIndex: number): string {
  if (!Number.isFinite(absIndex)) absIndex = 0;
  const idx = ((absIndex % 100) + 100) % 100; // clamp [0,99]
  const suffix = 200 + idx; // 200..299
  return `+14045550${suffix}`;
}

/** Pretty `(404) 555-02NN` form for `caller_number_format` field. */
function buildFccPhoneFormatted(absIndex: number): string {
  if (!Number.isFinite(absIndex)) absIndex = 0;
  const idx = ((absIndex % 100) + 100) % 100;
  const suffix = 200 + idx; // 200..299
  return `(404) 555-0${suffix}`;
}

/**
 * Calls-list sub-pool: 30 unique phones at indices [0..29]. 65 calls
 * round-robin through these (~2.2 calls per phone average — realistic
 * repeat-caller pattern).
 */
function buildCallPhone(callIndex: number): {
  e164: string;
  formatted: string;
} {
  const pool = ((callIndex % 30) + 30) % 30;
  return {
    e164: buildFccPhoneE164(pool),
    formatted: buildFccPhoneFormatted(pool),
  };
}

/** Form-submission sub-pool: 20 unique phones at indices [30..49]. */
function buildFormPhone(formIndex: number): {
  e164: string;
  formatted: string;
} {
  const pool = 30 + (((formIndex % 20) + 20) % 20);
  return {
    e164: buildFccPhoneE164(pool),
    formatted: buildFccPhoneFormatted(pool),
  };
}

/**
 * SMS sub-pool: 12 unique phones at indices [50..61]. The 12-conversation
 * fixture domain (per AC #6 `smsConversationsCount: 12`) maps 1:1 to phone
 * slots; the 25-message stream rotates these 12 conversations.
 *
 * Code Review Round 3 Blind Hunter #5 — JSDoc previously claimed a 30-phone
 * domain `[50..79]`, but only 12 are ever exercised. Sized the modulo to
 * the actual domain (12) so the function's rotation matches its docstring.
 * The reserved FCC sub-block remains `555-0250..0279` (30 slots) per AC #6;
 * future expansion can widen this modulo if conversation count grows.
 */
function buildSmsPhone(conversationIndex: number): {
  e164: string;
  formatted: string;
} {
  const pool = 50 + (((conversationIndex % 12) + 12) % 12);
  return {
    e164: buildFccPhoneE164(pool),
    formatted: buildFccPhoneFormatted(pool),
  };
}

/**
 * Tracking number sub-pool: 19 unique phones at indices [80..98]. Code
 * Review Round 2 Edge Hunter #9 — reserved slot 99 (`+14045550299`) for
 * the static `business_number` constant so per-call tracking phones can
 * never collide with the business number within a single fixture. Rotation
 * domain is 19, NOT 20, to enforce this.
 */
function buildTrackingPhone(absIndex: number): {
  e164: string;
  formatted: string;
} {
  const pool = 80 + (((absIndex % 19) + 19) % 19);
  return {
    e164: buildFccPhoneE164(pool),
    formatted: buildFccPhoneFormatted(pool),
  };
}

// ============================================================================
// Core generator
// ============================================================================

/**
 * Generate the Summit Legal Group CTM fixture.
 *
 * Produces ~111 records across 4 `_reportType` discriminators:
 *   - 65 calls-list rows  (per-call records, PII-safe synthetic)
 *   - 20 form-submissions rows  (FormReactor intake, PII-safe synthetic)
 *   - 25 sms rows  (omnichannel differentiator — templated PII-safe bodies)
 *   - 1 accounts-list stub row  (minimum-discovery, no widget binding)
 *   = 111 records total. AC #2 requires ≥ 110.
 *
 * The 9 widget presets in Story 22.4 AC #9 each find their backing
 * `_reportType` in this fixture. The build-to-sell `ctm-omnichannel-touch-
 * funnel` preset aggregates across all 3 wire-format discriminators.
 */
export function generateCtmFixtures(
  config: FixtureConfig,
): AdapterMetrics {
  const rng = createRng(config.seed);
  const accountId = config.accountId;

  // Date-range validation (mirrors CallRail Pass 1 Edge Hunter invariant).
  if (
    !(config.startDate instanceof Date) ||
    !Number.isFinite(config.startDate.getTime()) ||
    !(config.endDate instanceof Date) ||
    !Number.isFinite(config.endDate.getTime())
  ) {
    throw new Error(
      "generateCtmFixtures: config.startDate and config.endDate must be finite Date instances (UTC-constructed via Date.UTC or new Date(isoString) recommended)",
    );
  }
  if (config.startDate.getTime() > config.endDate.getTime()) {
    throw new Error(
      `generateCtmFixtures: invalid date range — config.endDate (${config.endDate.toISOString()}) must be on or after config.startDate (${config.startDate.toISOString()})`,
    );
  }

  // Code Review Round 1 Edge Hunter #4 — baseMetrics contract guard.
  // The AC #4-#6 invariants pin the row counts to 65/20/25. The generator
  // hardcodes these via CTM_FIXTURE_SOURCES + 25-message loop. If a future
  // edit changes `baseMetrics.totalCalls` / `smsCount` / `formSubmissionsCount`
  // in demo-accounts.ts (perhaps to demo a busier client), the generator's
  // output would silently diverge from baseMetrics. Detect that drift loudly
  // at generation time rather than discovering it through widget mismatches.
  const expectedTotalCalls =
    (config.baseMetrics.totalCalls as number | undefined) ?? 65;
  const expectedFormSubmissions =
    (config.baseMetrics.formSubmissionsCount as number | undefined) ?? 20;
  const expectedSms = (config.baseMetrics.smsCount as number | undefined) ?? 25;
  if (
    expectedTotalCalls !== 65 ||
    expectedFormSubmissions !== 20 ||
    expectedSms !== 25
  ) {
    throw new Error(
      `generateCtmFixtures: baseMetrics drift — generator hardcodes 65/20/25 records per AC #4-#6 ` +
        `but baseMetrics specifies ${expectedTotalCalls}/${expectedFormSubmissions}/${expectedSms}. ` +
        `Update demo-accounts.ts back to 65/20/25 OR refactor the generator to honor the new counts.`,
    );
  }

  const fixtureGeneratedAtMs = config.endDate.getTime();
  // Most-recent acceptable timestamp: 1 hour before fixtureGeneratedAt
  // (CTM near-real-time + 20-min webhook delay; matches Story 22.3 cache
  // TTL freshness convention).
  const recencyCeilingMs = fixtureGeneratedAtMs - 60 * 60 * 1000;
  // Oldest acceptable timestamp: 90-day window (NO retention clamp — CTM
  // DPA 2025 has no retention enforcement per AC #15).
  const recencyFloorMs =
    fixtureGeneratedAtMs - 90 * 24 * 60 * 60 * 1000;

  /** Clamp a candidate timestamp to within the recency window. */
  function clampRecency(candidateMs: number): number {
    if (candidateMs < recencyFloorMs) return recencyFloorMs;
    if (candidateMs > recencyCeilingMs) return recencyCeilingMs;
    return candidateMs;
  }

  const records: Array<Record<string, unknown>> = [];

  // ───────────────────────────────────────────────────────────────────
  // 1. calls-list (65 rows) — Story 22.3 `/calls.json`
  // ───────────────────────────────────────────────────────────────────
  // AC #4: source distribution 23/16/13/8/5 + answered/missed 56/9 +
  // voicemail subset (5 of answered) + PII-safe customer (FCC 0200/0229)
  // + audio-URL-omitted + likelihood distribution 22/18/15/10 + inline-
  // sale invariant (18 of 56 answered have sale object) + CI fields
  // populated when CI-entitled.
  let callIndex = 0;

  // Likelihood distribution buckets (Round 4 ADR-D — 4 quality bands).
  // 22 high (>=0.8) / 18 good (>=0.6) / 15 fair (>=0.4) / 10 low (<0.4).
  // Generate via index-deterministic assignment so the bucket invariant
  // holds exactly under the seed.
  //
  // Code Review Round 1 Edge Hunter #3 + Round 2 Edge Hunter #10 — IEEE-754
  // FP-boundary defense: each bucket clamps the random component to
  // (range - 0.011) so values land strictly inside the bucket interval
  // (e.g., high gets [0.80, 0.989] vs the bucket interval [0.80, 1.00)).
  // This guarantees the value-based bucket assertion in tests passes for
  // ANY seed, not just seed 42. Without this clamp, `0.60 + 0.199` could
  // round to `0.7990000000000001` which is still strictly below 0.80, but
  // `0.40 + 0.199` could round to `0.5990000000000001` which is also fine
  // (still below 0.60). The 0.011 margin is deliberately conservative;
  // raising to 0.001 (as a future "tighter" fix) would re-introduce the
  // FP-boundary leak that Round 1 fixed.
  function pickLikelihood(idx: number): number {
    const m = idx % 65;
    if (m < 22) {
      // high: 0.80..0.989 (well below 1.0 ceiling and above bucket boundary 0.80)
      return 0.80 + randomFloat(rng, 0, 0.189);
    }
    if (m < 22 + 18) {
      // good: 0.60..0.789 (strictly below 0.80 — no high-bucket leak)
      return 0.60 + randomFloat(rng, 0, 0.189);
    }
    if (m < 22 + 18 + 15) {
      // fair: 0.40..0.589 (strictly below 0.60 — no good-bucket leak)
      return 0.40 + randomFloat(rng, 0, 0.189);
    }
    // low: 0.05..0.389 (strictly below 0.40 — no fair-bucket leak)
    return 0.05 + randomFloat(rng, 0, 0.339);
  }

  // Voicemail-subset accounting: 5 voicemails carved out of the 56
  // answered rows (per AC #4 — voicemail is a sub-status of completed
  // in CTM's dial_status taxonomy). We promote the FIRST 5 answered
  // rows to `dial_status: "voicemail"` so the test can assert exactly
  // 5 voicemail + 51 completed + 9 no-answer = 65 total.
  let voicemailRemaining = 5;

  // Inline-sale accounting: 18 of 56 answered rows have a populated
  // sale object. We tag the FIRST 18 answered rows (in iteration order
  // across the source list) so the assertion runs deterministically.
  let salesRemaining = 18;

  // Per-call name + city are deterministic functions of `callIndex` so
  // the same phone always presents the same identity across repeat calls
  // (matches CallRail 22.2 Pass 3 review fix — demo realism invariant).
  // We bind name + city to the PHONE SLOT (callIndex % 30) so the 35
  // repeat callers share the same identity as their first call.
  for (const source of CTM_FIXTURE_SOURCES) {
    for (let s = 0; s < source.callCount; s++) {
      const isAnswered = s < source.answeredCount;
      const isVoicemail = isAnswered && voicemailRemaining > 0;
      if (isVoicemail) voicemailRemaining--;
      const dialStatus = isAnswered
        ? isVoicemail
          ? "voicemail"
          : "completed"
        : "no-answer";

      // Phone-slot binding (Pass 3 review fix carry-forward).
      const phoneSlot = callIndex % 30;
      const phone = buildCallPhone(phoneSlot);
      const callerName =
        CTM_FIXTURE_CALL_NAMES[phoneSlot % CTM_FIXTURE_CALL_NAMES.length]!;
      const callerCity =
        CTM_FIXTURE_CITIES[phoneSlot % CTM_FIXTURE_CITIES.length]!;

      // Recency: spread across 90-day window. Use `randomInt` for
      // deterministic offset.
      const offsetDays = randomInt(rng, 1, 89);
      const calledAtMs = clampRecency(
        recencyCeilingMs - offsetDays * 24 * 60 * 60 * 1000,
      );
      // CTM returns called_at with account-timezone offset; the production
      // transformer normalizes to UTC. We emit UTC ISO directly so the
      // transformer's `normalizeCtmTimestampToUtc` is a no-op.
      const calledAtIso = new Date(calledAtMs).toISOString();
      const unixTime = Math.floor(calledAtMs / 1000);

      // Duration distribution: legal-intake 60-300sec for answered,
      // 0 for unanswered, ring-only for no-answer.
      const ringTime = isAnswered ? randomInt(rng, 4, 12) : randomInt(rng, 8, 25);
      const talkTime = isAnswered ? randomInt(rng, 60, 300) : 0;
      const holdTime = isAnswered ? randomInt(rng, 0, 30) : 0;
      const waitTime = ringTime + holdTime;
      const duration = ringTime + talkTime + holdTime;

      const likelihood = pickLikelihood(callIndex);

      // Inline-sale: 18 of 56 answered rows. Only attach to "completed"
      // status (not voicemail) — sale data comes after a real conversation.
      const isCompleted = dialStatus === "completed";
      const hasSale = isCompleted && salesRemaining > 0;
      if (hasSale) salesRemaining--;
      const saleValue = hasSale ? randomInt(rng, 100, 1500) : 0;
      const saleScore = hasSale ? randomInt(rng, 6, 10) : 0;

      const trackingPhone = buildTrackingPhone(callIndex);

      // Tag list — 1-2 tags from a small safe bank.
      const safeTagBank: ReadonlyArray<string> = [
        "intake",
        "consultation",
        "follow-up",
        "case-eval",
        "referral",
      ];
      const tagCount = randomInt(rng, 1, 2);
      const tagList: string[] = [];
      for (let t = 0; t < tagCount; t++) {
        tagList.push(safeTagBank[(callIndex + t) % safeTagBank.length]!);
      }

      // CI-entitled fields (Round 4 ADR-D — fixture force-claims true).
      // ~70% of answered rows have populated CI per AC #4 realism.
      const ciEnabledForRow =
        isAnswered && callIndex % 10 < 7;
      const summary = ciEnabledForRow
        ? CTM_FIXTURE_SUMMARIES[
            callIndex % CTM_FIXTURE_SUMMARIES.length
          ]!
        : undefined;
      const transcription = ciEnabledForRow
        ? CTM_FIXTURE_TRANSCRIPTIONS[
            callIndex % CTM_FIXTURE_TRANSCRIPTIONS.length
          ]!
        : undefined;
      const agentInsights = ciEnabledForRow ? true : false;

      // Build the wire-format raw row (snake_case CTM v1 shape). Run
      // through `transformCtmCallRow` to guarantee record shape parity
      // with production. NOTE: NO `audio` field — the transformer would
      // strip it, but we omit at construction for clarity.
      const rawRow: Record<string, unknown> = {
        id: `demo-ctm-call-${callIndex + 1}`,
        account_id: accountId,
        sid: `CA${callIndex.toString().padStart(32, "0").slice(0, 32)}`,
        caller_number: phone.e164,
        caller_number_format: phone.formatted,
        name: htmlEscape(stripCsvInjectionPrefix(callerName)),
        email: "[redacted]",
        city: htmlEscape(stripCsvInjectionPrefix(callerCity)),
        state: "GA",
        country: "US",
        dial_status: dialStatus,
        talk_time: talkTime,
        ring_time: ringTime,
        duration,
        hold_time: holdTime,
        wait_time: waitTime,
        called_at: calledAtIso,
        unix_time: unixTime,
        is_new_caller: isAnswered && s < Math.floor(source.answeredCount * 0.65),
        // Code Review Round 1 Edge Hunter #8 — match baseMetrics exactly:
        // `redactedCallsCount: 4` (callIndex 0/20/40/60 = 4 rows)
        // `excludedCalls: 3` (callIndex 11/33/55 = 3 rows). Code Review
        // Round 2 Blind Hunter #4 — offset the `excluded` seed by 11 so
        // row 0 doesn't have BOTH redacted AND excluded set (the prior
        // `% 22 === 0` and `% 20 === 0` both fired on row 0, collapsing
        // the 7-row flagged pool to 6 distinct rows).
        redacted: callIndex % 20 === 0, // 4 rows / 65 (~6%) HIPAA-flagged
        excluded: callIndex % 22 === 11, // 3 rows / 65 (~5%), no row-0 overlap
        likelihood,
        source: htmlEscape(stripCsvInjectionPrefix(source.slug)),
        source_id: `src-${source.slug}-${callIndex + 1}`,
        tracking_number: trackingPhone.e164,
        tracking_label: htmlEscape(
          stripCsvInjectionPrefix(source.sourceLabel),
        ),
        // Code Review Round 1 Auditor #3 + Round 2 Edge Hunter #9 + Round 3
        // Blind Hunter #6 — CTM `business_number` lives in CTM's reserved FCC
        // tracking sub-pool (555-0280..0299) per AC #4 cross-platform PII-
        // disjointness AND intra-CTM uniqueness. `buildTrackingPhone(N)`
        // rotates over indices 80..98 ONLY (modulo 19), emitting 0280..0298.
        // Slot 99 (`+14045550299`) is reserved exclusively for this static
        // business_number — guaranteeing zero collision with any per-call
        // tracking number. Round 1 originally used 0280 which collided with
        // `buildTrackingPhone(0)`; Round 2 moved it to 0299 + tightened the
        // tracking-phone modulo to 19.
        business_number: "+14045550299",
        campaign: htmlEscape(
          stripCsvInjectionPrefix(
            source.slug === "google-paid"
              ? "Free Case Evaluation"
              : "Returning Client Follow-up",
          ),
        ),
        keyword: htmlEscape(
          stripCsvInjectionPrefix(
            source.slug === "google-paid"
              ? "personal injury lawyer atlanta"
              : "",
          ),
        ),
        paid: source.hasGclid
          ? {
              campaign: htmlEscape(
                stripCsvInjectionPrefix("Atlanta Legal — Search"),
              ),
              source: htmlEscape(stripCsvInjectionPrefix("google")),
              medium: htmlEscape(stripCsvInjectionPrefix(source.paidMedium)),
              keyword: htmlEscape(
                stripCsvInjectionPrefix("personal injury lawyer atlanta"),
              ),
            }
          : undefined,
        ga: source.hasGclid
          ? { gclid: `demo-gclid-${callIndex + 1}` }
          : undefined,
        tag_list: tagList.map((t) =>
          htmlEscape(stripCsvInjectionPrefix(t)),
        ),
        ...(ciEnabledForRow
          ? {
              transcription: htmlEscape(
                stripCsvInjectionPrefix(transcription!),
              ),
              summary: htmlEscape(stripCsvInjectionPrefix(summary!)),
              agent_insights: agentInsights,
            }
          : {}),
        ...(hasSale
          ? {
              sale: {
                conversion: true,
                value: saleValue,
                score: saleScore,
                sale_date: calledAtIso,
                service_rep_name: htmlEscape(
                  stripCsvInjectionPrefix(
                    `Rep ${(callIndex % 4) + 1}`,
                  ),
                ),
              },
            }
          : {}),
      };

      records.push(transformCtmCallRow(rawRow, { accountId }));
      callIndex++;
    }
  }

  // ───────────────────────────────────────────────────────────────────
  // 2. form-submissions (20 rows) — Story 22.3 `/forms.json`
  // ───────────────────────────────────────────────────────────────────
  // AC #5: form-name distribution 12/6/2 + first-form 15/20=75% +
  // PII-safe customer (FCC 0230/0249 DISJOINT from calls) + form URLs +
  // `custom` array invariant (2-4 {id, value} pairs).
  const formTypeEntries: string[] = [];
  for (const t of CTM_FIXTURE_FORM_TYPES) {
    for (let i = 0; i < t.count; i++) formTypeEntries.push(t.name);
  }

  for (let f = 0; f < 20; f++) {
    const isFirstForm = f < 15;
    const formName = formTypeEntries[f]!;
    const submitterName =
      CTM_FIXTURE_FORM_NAMES[f % CTM_FIXTURE_FORM_NAMES.length]!;
    const phone = buildFormPhone(f);

    const offsetDays = randomInt(rng, 1, 89);
    const createdAtMs = clampRecency(
      recencyCeilingMs - offsetDays * 24 * 60 * 60 * 1000,
    );
    const createdAtIso = new Date(createdAtMs).toISOString();

    const sourceForForm =
      CTM_FIXTURE_SOURCES[f % CTM_FIXTURE_SOURCES.length]!;

    // `custom` array: 2-4 {id, value} pairs per row with realistic
    // legal-intake field IDs (Round 5 Edge Case Hunter AC #5).
    const customFieldCount = 2 + (f % 3); // 2, 3, or 4
    const custom: Array<{ id: number; value: string }> = [];
    const customFieldBank: ReadonlyArray<{ id: number; value: string }> = [
      { id: 12345, value: "Personal Injury" },
      { id: 12346, value: "Within 30 days" },
      { id: 12347, value: "Cobb County" },
      { id: 12348, value: "Referred by friend" },
    ];
    for (let c = 0; c < customFieldCount; c++) {
      const f0 = customFieldBank[c % customFieldBank.length]!;
      custom.push({
        id: f0.id,
        value: htmlEscape(stripCsvInjectionPrefix(f0.value)),
      });
    }

    const rawRow: Record<string, unknown> = {
      id: `demo-ctm-form-${f + 1}`,
      created_at: createdAtIso,
      form_name: htmlEscape(stripCsvInjectionPrefix(formName)),
      form_url: `https://${CTM_SUMMIT_LEGAL_TARGET}/intake-form`,
      landing_page_url: `https://${CTM_SUMMIT_LEGAL_TARGET}/`,
      referring_url: `https://${CTM_SUMMIT_LEGAL_TARGET}/about`,
      source: htmlEscape(stripCsvInjectionPrefix(sourceForForm.slug)),
      keywords: "",
      paid: sourceForForm.hasGclid
        ? {
            campaign: htmlEscape(
              stripCsvInjectionPrefix("Atlanta Legal — Search"),
            ),
            source: htmlEscape(stripCsvInjectionPrefix("google")),
            medium: htmlEscape(
              stripCsvInjectionPrefix(sourceForForm.paidMedium),
            ),
          }
        : undefined,
      name: htmlEscape(stripCsvInjectionPrefix(submitterName)),
      email: "[redacted]",
      caller_number: phone.e164,
      caller_number_format: phone.formatted,
      city: htmlEscape(
        stripCsvInjectionPrefix(
          CTM_FIXTURE_CITIES[f % CTM_FIXTURE_CITIES.length]!,
        ),
      ),
      state: "GA",
      postal_code: "30303",
      country: "US",
      first_form: isFirstForm,
      tag_list: ["form-intake"],
      custom,
    };

    records.push(transformCtmFormSubmissionRow(rawRow, { accountId }));
  }

  // ───────────────────────────────────────────────────────────────────
  // 3. sms (25 rows) — Story 22.3 `/sms.json`
  // ───────────────────────────────────────────────────────────────────
  // AC #6: direction distribution 7 inbound / 18 outbound + 12 distinct
  // conversation threads + PII-safe FCC 0250/0279 + CRITICAL SMS body
  // PII redaction (5-template inbound + 3-template outbound).
  //
  // Thread assignment: 12 distinct conversation threads across 25 messages.
  // Phone slots 0..11 are the 12 unique threads; we round-robin the 25
  // messages through these 12 slots so phones recur (some 2-3× — realistic
  // back-and-forth SMS thread pattern). Direction is interleaved to achieve
  // 7/18 inbound/outbound split.
  //
  // Code Review Round 1 Auditor #5 + Round 2 Blind Hunter #7 — INBOUND
  // positions chosen so that `m % 12` produces 7 DISTINCT phone slots
  // (was [0,4,8,12,16,20,24] which collapsed to only 3 phones {0,4,8}
  // under % 12 aliasing). New spread `[0,3,7,10,14,18,22]` yields slots
  // {0,3,7,10,2,6,10} → 6 distinct phones for 7 inbound messages
  // (1 thread sees 2 inbound — realistic back-and-forth). Distribution
  // is still even (gaps of 3-4 messages between inbound across the
  // 25-message stream).
  const inboundIndexes = new Set([0, 3, 7, 10, 14, 18, 22]);
  for (let m = 0; m < 25; m++) {
    const direction = inboundIndexes.has(m) ? "inbound" : "outbound";
    // Conversation thread index ∈ [0, 11] — round-robin so exactly 12
    // distinct phones span 25 messages. Slots 0..11 each appear at least
    // once (messages 0..11) and again for messages 12..23; slot 0 appears
    // a third time for message 24.
    const threadIndex = m % 12;
    const phone = buildSmsPhone(threadIndex);
    const trackingPhone = buildTrackingPhone(threadIndex);

    const messageBody =
      direction === "inbound"
        ? CTM_FIXTURE_INBOUND_SMS_MESSAGES[
            m % CTM_FIXTURE_INBOUND_SMS_MESSAGES.length
          ]!
        : CTM_FIXTURE_OUTBOUND_SMS_MESSAGES[
            m % CTM_FIXTURE_OUTBOUND_SMS_MESSAGES.length
          ]!;

    const offsetDays = randomInt(rng, 1, 89);
    const createdAtMs = clampRecency(
      recencyCeilingMs - offsetDays * 24 * 60 * 60 * 1000,
    );
    const createdAtIso = new Date(createdAtMs).toISOString();

    const rawRow: Record<string, unknown> = {
      id: `demo-ctm-sms-${m + 1}`,
      created_at: createdAtIso,
      direction,
      caller_number: phone.e164,
      caller_number_format: phone.formatted,
      tracking_number: trackingPhone.e164,
      tracking_number_format: trackingPhone.formatted,
      // CRITICAL — SMS bodies are templated PII-safe per AC #6 Round 3
      // Red Team Attack #1 invariant. NEVER real customer content.
      message: htmlEscape(stripCsvInjectionPrefix(messageBody)),
      source: htmlEscape(stripCsvInjectionPrefix("FormReactor")),
      tag_list: ["sms-confirmation"],
    };
    // NO-DELIVERY-RECEIPT-FIELDS invariant (Round 5 Edge Case Hunter):
    // do NOT emit `delivered_at` / `delivery_status` / `failed_reason`.

    records.push(transformCtmSmsRow(rawRow, { accountId }));
  }

  // ───────────────────────────────────────────────────────────────────
  // 4. accounts-list stub (1 row) — connect-flow probe response shape
  // ───────────────────────────────────────────────────────────────────
  // AC #7: MIN-STUB — `discoverEndpoints` consumer; NO widget binding
  // in Story 22.4. A future Story 22.x agency-key picker widget would
  // extend this row.
  records.push({
    _reportType: "accounts-list",
    id: accountId,
    name: htmlEscape(stripCsvInjectionPrefix(CTM_SUMMIT_LEGAL_ACCOUNT_NAME)),
    status: "active",
    timezone: "America/New_York",
    currency: "USD",
  });

  // ───────────────────────────────────────────────────────────────────
  // metadata + return
  // ───────────────────────────────────────────────────────────────────
  // AC #2 + AC #8 force-stamps:
  //   - region: "us"               (Round 2 Pre-mortem C — NEVER "eu")
  //   - keyScope: "account"         (Round 2 Pre-mortem B)
  //   - conversationIntelligenceEntitled: true (Round 4 ADR-D)
  //   - dataFreshnessLagHours: 1   (CTM near-real-time)
  //   - planTier: "marketing-pro"
  //   - allowedAccountIds: [accountId]
  //   - warnings: []                (Round 1 QA Test Architect — explicit
  //                                  empty array, NEVER undefined)
  const fixtureGeneratedAt = new Date(fixtureGeneratedAtMs).toISOString();
  const dataFinalizedThrough = new Date(recencyCeilingMs).toISOString();

  return {
    platform: "calltrackingmetrics",
    accountId,
    dateRange: { start: config.startDate, end: config.endDate },
    records,
    fetchedAt: config.endDate,
    recordCount: records.length,
    metadata: {
      accountId,
      accountName: CTM_SUMMIT_LEGAL_ACCOUNT_NAME,
      currencyCode: "USD",
      timeZone: "America/New_York",
      region: "us",
      keyScope: "account",
      dataFreshnessLagHours: 1,
      dataFinalizedThrough,
      fixtureGeneratedAt,
      fixtureSchemaVersion: CTM_FIXTURE_SCHEMA_VERSION,
      conversationIntelligenceEntitled: true,
      planTier: "marketing-pro",
      allowedAccountIds: [accountId],
    },
    warnings: [],
  };
}
